import * as React from "react"
import { useCallback, useEffect, useRef } from "react"
import { Action } from "@tenforce/toolbox-button-push-react"
import { Symbols } from "@tenforce/toolbox-fontmap"
import { Modal, Content } from "@tenforce/toolbox-modal-react"

import {
    ContextualHelpDictionary,
    ContextualHelpTerm,
} from "../../../../../models"
import Term from "./Term"

interface Props {
    isContentShown: boolean
    isAnyContentShown: boolean
    target: Element
    dictionarySources: ContextualHelpDictionary[]
    toggleTooltip: (element: Element | null, term: ContextualHelpTerm) => void
}

export const Tooltip = ({
    dictionarySources,
    isContentShown,
    isAnyContentShown,
    target,
    toggleTooltip,
}: Props): JSX.Element => {
    const contentRef = useRef<HTMLDivElement>(null)
    const tooltipActionRef = useRef<HTMLButtonElement>(null)
    const targetRef = useRef<HTMLElement>(target as HTMLElement)

    const terms = dictionarySources.flatMap((ds) => ds.terms)
    // We currently only support a single dictionary source. In the future, this should select the proper one.
    const selectedDictionarySource = dictionarySources[0]

    const toggleVisibility = useCallback(() => {
        toggleTooltip(
            isContentShown ? null : target,
            dictionarySources[0].terms[0],
        )
    }, [toggleTooltip, isContentShown, target, dictionarySources])

    if (!terms || terms.length === 0) {
        return <></>
    }

    const topPadding = Number.parseInt(
        window
            .getComputedStyle(targetRef.current)
            ?.paddingTop?.replace("px", "") ?? "0",
    )

    useEffect(() => {
        if (isContentShown) {
            setTimeout(() => {
                targetRef?.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                })
            }, 500)
        }
    }, [isContentShown])

    return (
        <Content
            isShown={true}
            targetRef={targetRef}
            shouldOptimizeHorizontalPosition={false}
            shouldOptimizeVerticalPosition={false}
            shouldRepositionOnTriggerResize={true}
            shouldRepositionOnContentResize={true}
            shouldRepositionOnResize={true}
            shouldRepositionOnScroll={true}
            shouldScrollToContent={false}
            shouldScrollBackToOrigin={false}
            preferredXPositioning={"right"}
            preferredYPositioning={"top"}
            horizontalOffset={10}
            verticalOffset={topPadding}
            hasMinWidth={false}
            repositionTimer={500}
            extraClasses={"tw-z-1"}
            data-ft-component="tooltip"
            data-ft-dictionary-source-count={dictionarySources.length}
            data-ft-is-content-shown={isContentShown}
            data-ft-selected-dictionary-code={selectedDictionarySource.code}
            data-ft-term-count={terms.length}
            data-ft-terms={terms.map((term) => term.code).join(",")}
        >
            <>
                <Action
                    data-ft-component="tooltip-trigger"
                    extraClasses={`bg-white tw-rounded-full tw-relative ${
                        !isContentShown && isAnyContentShown
                            ? "tw-opacity-25"
                            : ""
                    }`}
                    onClick={toggleVisibility}
                    ref={tooltipActionRef}
                >
                    {Symbols.Tooltip}
                </Action>
                <Modal
                    isShown={isContentShown}
                    contentProps={{
                        shouldRepositionOnScroll: true,
                        shouldOptimizeHorizontalPosition: true,
                        shouldOptimizeVerticalPosition: true,
                        shouldRepositionOnTriggerResize: true,
                        shouldRepositionOnContentResize: true,
                        shouldRepositionOnResize: true,
                        shouldScrollBackToOrigin: false,
                        shouldScrollToContent: false,
                        repositionTimer: 500,
                    }}
                    containerProps={{
                        targetRef: tooltipActionRef,
                        onClose: toggleVisibility,
                    }}
                    overlayProps={{
                        "data-ft-component": "overlay-tooltip",
                        extraClasses: "!tw-opacity-10",
                    }}
                >
                    <div
                        className={
                            "bg-default tw-min-w-1/2-screen sm:tw-min-w-1/3-screen tw-p-3"
                        }
                        data-ft-component="tooltip-content"
                        ref={contentRef}
                    >
                        {terms.map(
                            (term, index): JSX.Element => (
                                <Term
                                    key={term.code}
                                    shouldShowSeparator={index > 0}
                                    term={term}
                                />
                            ),
                        )}
                    </div>
                </Modal>
            </>
        </Content>
    )
}

export default Tooltip
